/**
 * Compact ACC project / model context in the app header — stays visible while
 * the user scrolls the left requirements panel.
 */
import { useMemo } from "preact/hooks";
import { isAuthDisabled } from "../auth/apsConfig";
import { inferAccProjectProfile } from "../lib/inferAccProjectProfile";
import { useAccBrowseStore } from "../stores/accBrowseStore";

function Sep() {
  return (
    <span aria-hidden style={{ color: "var(--fc2-text-dim)", opacity: 0.45 }}>
      ·
    </span>
  );
}

export function AccHeaderContext() {
  const hub = useAccBrowseStore(s => s.hub);
  const project = useAccBrowseStore(s => s.project);
  const projectDetails = useAccBrowseStore(s => s.projectDetails);
  const selection = useAccBrowseStore(s => s.selection);

  const profile = useMemo(() => {
    if (!hub || !project) return null;
    return inferAccProjectProfile({
      hub,
      project,
      platform: projectDetails?.platform,
      selectedFileName: selection?.fileName,
      folderPath: selection?.folderPath,
    });
  }, [hub, project, projectDetails?.platform, selection]);

  if (isAuthDisabled() || !hub || !project) return null;

  const jurisdiction = profile?.locationLabel ?? "Jurisdiction TBD";
  const codeBasis = profile?.codeBasis;
  const hubLabel =
    hub.region && hub.region !== hub.name
      ? `${hub.name} (${hub.region})`
      : hub.name;

  const tooltipLines = [
    selection
      ? `Model: ${selection.fileName}`
      : `Project: ${project.name} — pick a Revit model in the left panel`,
    `Hub: ${hubLabel}`,
    selection?.folderPath ? `Folder: ${selection.folderPath}` : null,
    codeBasis ? `Code basis: ${codeBasis}` : null,
  ].filter(Boolean);

  const metaParts = selection
    ? [project.name, hub.name, jurisdiction]
    : [hub.name, jurisdiction];

  return (
    <div
      title={tooltipLines.join("\n")}
      style={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginLeft: 4,
        paddingLeft: 16,
        borderLeft: "1px solid var(--fc2-border)",
        fontSize: 12,
        lineHeight: 1.3,
        color: "var(--fc2-text-dim)",
      }}
    >
      {selection ? (
        <>
          <span
            aria-hidden
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 18,
              height: 18,
              borderRadius: 3,
              background: "#2563eb",
              color: "#fff",
              fontSize: 10,
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            R
          </span>
          <span
            style={{
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              color: "var(--fc2-text)",
              fontWeight: 600,
            }}
          >
            {selection.fileName}
          </span>
        </>
      ) : (
        <span
          style={{
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: "var(--fc2-text)",
            fontWeight: 600,
          }}
        >
          {project.name}
        </span>
      )}

      <span
        style={{
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {metaParts.map((part, i) => (
          <span key={part}>
            {i > 0 ? <Sep /> : null}
            {part}
          </span>
        ))}
        {!selection && (
          <>
            <Sep />
            <span style={{ fontStyle: "italic", opacity: 0.85 }}>
              no model selected
            </span>
          </>
        )}
      </span>
    </div>
  );
}
